import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RotateCw, RefreshCcw } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const STAGES = [
  ["uploaded", "Uploaded", "bg-stone-100 text-stone-700"],
  ["queued", "Queued", "bg-sky-100 text-sky-800"],
  ["building", "Building", "bg-amber-100 text-amber-800"],
  ["checked", "Checked", "bg-emerald-100 text-emerald-800"],
  ["attention", "Needs attention", "bg-red-100 text-red-800"],
];
const tone = Object.fromEntries(STAGES.map(([id, label, c]) => [id, [label, c]]));

const btn = "inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-xs transition-colors disabled:opacity-40";

const Actions = ({ it, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const call = async (path, ok) => {
    setBusy(true);
    try { await http.post(path); toast.success(ok); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    setConfirm(false);
  };
  const retry = () => call(it.kit_id ? `/kits/${it.kit_id}/retry` : `/imports/${it.id}/retry`, it.kit_id ? "Retrying — carrying on from where it stopped" : "Upload read again and added to the build queue");
  return (
    <>
      {it.can_retry && <button data-testid={`upload-retry-${it.id}`} disabled={busy} onClick={retry} className={`${btn} border-stone-300 text-stone-700 hover:bg-stone-100`}>
        {busy && !confirm ? <Loader2 size={11} className="animate-spin" /> : <RotateCw size={11} />} Retry</button>}
      {it.can_restart && <button data-testid={`upload-restart-${it.id}`} disabled={busy} onClick={() => setConfirm(true)} className={`${btn} border-red-200 text-red-700 hover:bg-red-50`}>
        <RefreshCcw size={11} /> Start over</button>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`upload-restart-dialog-${it.id}`} className="bg-white text-[#2B2A26]">
          <AlertDialogHeader>
            <AlertDialogTitle>Start {it.name} over?</AlertDialogTitle>
            <AlertDialogDescription>Everything written for this kit so far is thrown away and it is rebuilt from your uploaded file. This uses AI credit again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`upload-restart-cancel-${it.id}`} className="bg-white">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid={`upload-restart-confirm-${it.id}`} disabled={busy} onClick={(e) => { e.preventDefault(); call(`/kits/${it.kit_id}/import-restart`, "Starting over from your file"); }} className="bg-red-700 text-white hover:bg-red-800">Start over</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const Row = ({ it, onDone }) => {
  const [label, c] = tone[it.stage];
  const name = <b className="text-[#2B2A26]">{it.name}</b>;
  return (
    <li data-testid={`upload-row-${it.id}`} className="flex flex-wrap items-center gap-2 border-t border-stone-100 py-1.5 text-sm">
      <span data-testid={`upload-stage-${it.id}`} className={`rounded-sm px-1.5 text-xs font-bold ${c}`}>{label}</span>
      {it.kit_id ? <Link to={`/kits/${it.kit_id}`} className="hover:underline">{name}</Link> : name}
      <span className="min-w-0 flex-1 truncate text-stone-500">{it.file && it.file !== it.name ? `${it.file} · ` : ""}{it.note}</span>
      {it.stage === "attention" && <Actions it={it} onDone={onDone} />}
    </li>
  );
};

export const UploadProgress = () => {
  const [d, setD] = useState(null);
  const [stage, setStage] = useState("all");
  const load = useCallback(() => http.get("/imports/progress").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!d) return <p data-testid="upload-progress-loading" className="text-sm text-stone-400">Loading uploads…</p>;
  if (!d.items.length) return <p data-testid="upload-progress-empty" className="text-sm text-stone-500">No draft uploads yet.</p>;
  const shown = stage === "all" ? d.items : d.items.filter((i) => i.stage === stage);
  return (
    <div data-testid="upload-progress" className="rounded-xl border border-stone-200 bg-white px-5 py-4">
      <div className="flex flex-wrap gap-2 mb-2">
        {[["all", "All", "bg-stone-100 text-stone-700"], ...STAGES].map(([id, label]) => (
          <button key={id} data-testid={`upload-filter-${id}`} onClick={() => setStage(id)} aria-pressed={stage === id}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${stage === id ? "bg-[#2B2A26] text-white border-[#2B2A26]" : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}>
            {label} <span className="ml-1 font-mono-ui opacity-70">{id === "all" ? d.items.length : d.counts[id]}</span>
          </button>
        ))}
      </div>
      <ul className="max-h-80 overflow-y-auto">{shown.map((it) => <Row key={it.id} it={it} onDone={load} />)}</ul>
      {!shown.length && <p data-testid="upload-stage-empty" className="text-sm text-stone-500 py-2">No uploads at this stage.</p>}
    </div>
  );
};
